/**
 * Authentication Store
 *
 * Manages user session state. Firebase provides auth + the ID token; the backend account is the
 * source of truth for the user's identity (read via GetMyAccount). Sign-in and sign-up happen
 * on the rrecce web app, which shares the Firebase session with this one.
 */

import { create } from 'zustand';
import {
  firebaseAuthReady,
  currentFirebaseUser,
  getFirebaseClaims,
  refreshFirebaseIdToken,
  firebaseSignOut,
  onFirebaseUserChanged,
} from '../services/firebaseClient.js';
import { verifyEmail as verifyEmailRpc, getMyAccount } from '../services/accountsClient.js';
import { connectRealtime, disconnectRealtime } from '../services/realtime.js';
import { usePresetsStore } from './presetsStore.js';
import { useRefImagesStore } from './refImagesStore.js';
import { useSetDressingRefsStore } from './setDressingRefsStore.js';
import { useAppearanceStore } from './appearanceStore.js';
import { clearCache as clearStorageCache } from '../services/storageUrls.js';
import { fetchSubscriptionStatus } from '../services/visionBillingClient.js';
import { fetchVisionPreferences } from '../services/visionAccountClient.js';
import {
  beginWebLogout,
  clearWebSessionStatus,
  isWebLogoutPending,
  onWebLogout,
} from '../services/webSession.js';
import { currentVisionPath, replaceWithWebApp, webReactivateUrl } from '../utils/webAppLinks.js';

// In-flight promise deduplication (prevents N concurrent API calls during batch events)
let _subscriptionPromise = null;
let authEpoch = 0;
let pendingSignOuts = 0;
let sessionUid = null;

// The last account of a Firebase user, to render at once on a reload while the backend confirms it
const USER_CACHE_KEY = 'rrecce_user_cache';

function getCachedUser(uid) {
  try {
    const cached = JSON.parse(localStorage.getItem(USER_CACHE_KEY));
    return cached?.uid === uid && cached.user ? cached.user : null;
  } catch { return null; }
}

function setCachedUser(user) {
  try {
    if (!user || !sessionUid) localStorage.removeItem(USER_CACHE_KEY);
    else if (user.id !== sessionUid) localStorage.setItem(USER_CACHE_KEY, JSON.stringify({ uid: sessionUid, user }));
  } catch { /* quota exceeded or private browsing — non-critical */ }
}

// Its id is the Firebase uid, which does NOT match the backend account_id, so it is never remembered
function userFromFirebase(fbUser) {
  return {
    id: fbUser.uid,
    email: fbUser.email,
    display_name: fbUser.displayName || '',
    avatar_path: null,
  };
}

function loadAppearance(epoch) {
  fetchVisionPreferences().then((prefs) => {
    if (prefs && epoch === authEpoch) useAppearanceStore.getState().hydrate(prefs);
  }, () => {});
}

async function readAccount() {
  try {
    return (await getMyAccount()) ?? null;
  } catch {
    return null;
  }
}

function isActive(claims) {
  return claims.is_backend_verified && claims.status === 'Active';
}

// Promote the backend account to Active once the Firebase email is verified, then refresh the
// token so it carries the updated claims. Skipped when the claims already show it active, so a
// session restore on every load doesn't re-issue the RPC.
async function ensureBackendVerified(email, claims) {
  if (isActive(claims)) return;

  await verifyEmailRpc(email);
  await refreshFirebaseIdToken();
}

function clearLocalSession() {
  disconnectRealtime();
  setCachedUser(null);
  sessionUid = null;
  usePresetsStore.getState().reset();
  useRefImagesStore.getState().reset();
  useSetDressingRefsStore.getState().reset();
  useAppearanceStore.getState().reset();
  clearStorageCache();
}

export const useAuthStore = create((set, get) => ({
  user: null,
  isAuthenticated: false,
  isInitialized: false, // Track if we've checked auth at least once
  isLoggedOut: false, // The session ended here or in another tab, rather than never having started
  authError: null,

  /**
   * Check authentication status on app mount. A verified Firebase session is the backend
   * identity; without one the user is unauthenticated.
   */
  checkAuth: async () => {
    if (pendingSignOuts) return null;
    const epoch = ++authEpoch;
    if (get().authError) set({ authError: null });
    try {
      return await restoreSession(epoch, set, get);
    } catch (error) {
      if (epoch !== authEpoch) return null;
      disconnectRealtime();
      sessionUid = null;
      set({ user: null, isAuthenticated: false, isInitialized: false, authError: error });
      return null;
    }
  },

  /**
   * Refresh credit balances. GetSubscriptionStatus owns the subscription/credit/storage
   * fields, so credit and storage refreshes both route through it.
   */
  refreshCredits: async () => {
    return get().refreshSubscriptionStatus();
  },

  /**
   * Refresh storage usage. Routes through GetSubscriptionStatus, which owns the storage fields.
   */
  refreshStorage: async () => {
    return get().refreshSubscriptionStatus();
  },

  /**
   * Merge a partial subscription patch into the user immediately (optimistic). Used after an
   * immediate plan change to reflect Stripe's confirmed tier at once, before the webhook has
   * applied the durable state that refreshSubscriptionStatus then reconciles.
   */
  applySubscriptionPatch: (patch) => {
    const user = get().user;
    if (!user) return;
    const merged = { ...user, ...patch };
    setCachedUser(merged);
    set({ user: merged });
  },

  /**
   * Refresh subscription status from the rrecce backend over gRPC and merge into user state.
   */
  refreshSubscriptionStatus: async () => {
    const owner = sessionUid;
    if (_subscriptionPromise?.owner === owner) return _subscriptionPromise.promise;
    const promise = (async () => {
      try {
        const data = await fetchSubscriptionStatus();
        const user = get().user;
        if (user && data && sessionUid === owner) {
          const merged = { ...user, ...data };
          setCachedUser(merged);
          set({ user: merged });
        }
      } catch (_) {
        // Non-critical — don't break UX if the subscription status refresh fails
      }
    })();
    _subscriptionPromise = { owner, promise };
    try {
      await promise;
    } finally {
      if (_subscriptionPromise?.promise === promise) _subscriptionPromise = null;
    }
  },

  /**
   * Sign out of the shared Firebase session and of the web app's cached copy of it, clearing
   * local state whether or not the sign-out request succeeds. The routes then leave for the web
   * sign-in page (ProtectedRoute and RootRedirect act on isLoggedOut).
   */
  logout: async () => {
    authEpoch += 1;
    pendingSignOuts += 1;
    clearStorageCache();
    disconnectRealtime();
    beginWebLogout();
    try {
      await firebaseSignOut();
    } finally {
      clearWebSessionStatus();
      clearLocalSession();
      set({ user: null, isAuthenticated: false, isInitialized: true, isLoggedOut: true, authError: null });
      pendingSignOuts -= 1;
    }
  },
}));

async function restoreSession(epoch, set, get) {
  await firebaseAuthReady();
  if (epoch !== authEpoch) return null;

  const fbUser = currentFirebaseUser();
  if (!fbUser || !fbUser.emailVerified || isWebLogoutPending()) {
    clearLocalSession();
    set({ user: null, isAuthenticated: false, isInitialized: true });
    return null;
  }

  sessionUid = fbUser.uid;
  const claims = await getFirebaseClaims();
  if (epoch !== authEpoch) return null;
  if (claims.status === 'Deleted') {
    clearLocalSession();
    set({ user: null, isAuthenticated: false, isInitialized: false });
    replaceWithWebApp(webReactivateUrl(currentVisionPath()));
    return null;
  }

  const cached = isActive(claims) ? getCachedUser(fbUser.uid) : null;
  if (cached && !get().isAuthenticated) set({ user: cached, isAuthenticated: true, isLoggedOut: false });

  await ensureBackendVerified(fbUser.email, claims);
  if (epoch !== authEpoch) return null;
  loadAppearance(epoch);
  const account = await readAccount();
  if (epoch !== authEpoch) return null;
  let user;
  if (account) {
    const prev = get().user;
    user = prev && prev.id === account.id ? { ...prev, ...account } : account;
    setCachedUser(user);
  } else {
    user = getCachedUser(fbUser.uid);
    if (!user) {
      setCachedUser(null);
      user = userFromFirebase(fbUser);
    }
  }
  set({ user, isAuthenticated: true, isLoggedOut: false, isInitialized: true });
  connectRealtime(user.id);
  get().refreshSubscriptionStatus();
  return user;
}

function endSessionElsewhere() {
  authEpoch += 1;
  clearLocalSession();
  useAuthStore.setState({ user: null, isAuthenticated: false, isInitialized: true, isLoggedOut: true, authError: null });
}

function switchAccount() {
  clearLocalSession();
  useAuthStore.setState({ user: null, isAuthenticated: false, isInitialized: false, authError: null });
  useAuthStore.getState().checkAuth();
}

/**
 * Follows the shared session while the app runs: a sign-out or an account switch made in another
 * tab or in the web app drops this session. Returns the unsubscribe.
 */
export function watchSession() {
  const stopFirebase = onFirebaseUserChanged((fbUser) => {
    if (sessionUid == null || fbUser?.uid === sessionUid) return;
    if (fbUser) switchAccount();
    else endSessionElsewhere();
  });
  const stopWebLogout = onWebLogout(endSessionElsewhere);
  return () => {
    stopFirebase();
    stopWebLogout();
  };
}
